import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import fetchWrapper from '@/services/fetchWrapper.service';

import {
  getSearchRandomizedFeedState,
  SearchRandomizedFeedsContext,
} from './searchRandomizedFeeds.context';

export default function SearchRandomizedFeedsProvider({ children }) {
  const [feeds, setFeeds] = useState({});
  const feedsRef = useRef({});
  const mountedRef = useRef(true);
  const generationRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const setFeed = useCallback((feedKey, nextFeed) => {
    const nextFeeds = {
      ...feedsRef.current,
      [feedKey]: nextFeed,
    };

    feedsRef.current = nextFeeds;
    setFeeds(nextFeeds);
  }, []);

  const resetFeeds = useCallback(() => {
    generationRef.current += 1;
    feedsRef.current = {};
    setFeeds({});
  }, []);

  const loadFeed = useCallback(async ({
    feedKey,
    endpoint,
    limit,
    injectToken = false,
    optionalToken = true,
    errorMessage,
    force = false,
  }) => {
    const existingFeed = getSearchRandomizedFeedState(feedsRef.current, feedKey);
    if (existingFeed.hasRequested && !force) {
      return;
    }

    setFeed(feedKey, {
      hasRequested: true,
      loading: true,
      loadingMore: false,
      data: [],
      listLengthHint: 0,
      hasMore: false,
      cursor: null,
      seed: null,
      error: null,
    });
    const requestGeneration = generationRef.current;

    try {
      const { response, data } = await fetchWrapper.get(
        `${endpoint}?browse_seed=random&limit=${limit}`,
        { injectToken, optionalToken }
      );

      if (!mountedRef.current || requestGeneration !== generationRef.current) {
        return;
      }

      if (response.ok && Array.isArray(data?.data)
        && typeof data.has_more === 'boolean'
        && (data.data.length === 0 || typeof data.browse_seed === 'string')
        && (!data.has_more || typeof data.next_cursor === 'string')) {
        setFeed(feedKey, {
          hasRequested: true,
          loading: false,
          loadingMore: false,
          data: data.data,
          listLengthHint: data.data.length + (data.has_more ? 1 : 0),
          hasMore: data.has_more,
          cursor: data.next_cursor,
          seed: data.browse_seed,
          error: null,
        });
        return;
      }

      setFeed(feedKey, {
        hasRequested: true,
        loading: false,
        loadingMore: false,
        data: [],
        listLengthHint: 0,
        hasMore: false,
        cursor: null,
        seed: null,
        error: errorMessage,
      });
    } catch (error) {
      if (!mountedRef.current || requestGeneration !== generationRef.current) {
        return;
      }

      setFeed(feedKey, {
        hasRequested: true,
        loading: false,
        loadingMore: false,
        data: [],
        listLengthHint: 0,
        hasMore: false,
        cursor: null,
        seed: null,
        error: error?.message || errorMessage,
      });
    }
  }, [setFeed]);

  const loadMoreFeed = useCallback(async ({ feedKey, endpoint, limit, injectToken = false, optionalToken = true, errorMessage }) => {
    const current = getSearchRandomizedFeedState(feedsRef.current, feedKey);
    if (!current.hasRequested || current.loading || current.loadingMore || !current.hasMore) {
      return;
    }

    const requestGeneration = generationRef.current;
    setFeed(feedKey, { ...current, loadingMore: true, error: null });
    try {
      const seen = new Set(current.data.map((item) => String(item._id)));
      const additions = [];
      let cursor = current.cursor;
      let hasMore = true;

      while (hasMore && additions.length === 0) {
        const { response, data } = await fetchWrapper.get(
          `${endpoint}?browse_seed=${encodeURIComponent(current.seed)}&browse_cursor=${encodeURIComponent(cursor)}&limit=${limit}`,
          { injectToken, optionalToken }
        );
        if (!mountedRef.current || requestGeneration !== generationRef.current) return;
        if (!response.ok || !Array.isArray(data?.data)
          || typeof data.has_more !== 'boolean'
          || (data.has_more && (typeof data.next_cursor !== 'string' || data.next_cursor === cursor))) {
          setFeed(feedKey, { ...feedsRef.current[feedKey], loadingMore: false, error: errorMessage });
          return;
        }

        data.data.forEach((item) => {
          const id = String(item._id);
          if (!seen.has(id)) {
            seen.add(id);
            additions.push(item);
          }
        });
        cursor = data.next_cursor;
        hasMore = data.has_more;
      }

      const latest = feedsRef.current[feedKey];
      const nextData = [...latest.data, ...additions];
      setFeed(feedKey, {
        ...latest,
        loadingMore: false,
        data: nextData,
        listLengthHint: nextData.length + (hasMore ? 1 : 0),
        hasMore,
        cursor,
        error: null,
      });
    } catch (error) {
      if (!mountedRef.current || requestGeneration !== generationRef.current) return;
      setFeed(feedKey, { ...feedsRef.current[feedKey], loadingMore: false, error: error?.message || errorMessage });
    }
  }, [setFeed]);

  const value = useMemo(() => ({ feeds, loadFeed, loadMoreFeed, resetFeeds }), [feeds, loadFeed, loadMoreFeed, resetFeeds]);

  return (
    <SearchRandomizedFeedsContext.Provider value={value}>
      {children}
    </SearchRandomizedFeedsContext.Provider>
  );
}
